import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth, isAccount } from "../context/AuthContext";
import { useSport } from "../context/SportContext";
import { Logo } from "../components/Logo";
import { TeamBadge } from "../components/TeamBadge";
import { Button } from "../components/ui/button";
import { Check, MagnifyingGlass, SoccerBall, TennisBall, Basketball } from "@phosphor-icons/react";
import { toast } from "sonner";

const SPORTS = [
  { key: "foot", label: "Football", Icon: SoccerBall, color: "#00E676" },
  { key: "tennis", label: "Tennis", Icon: TennisBall, color: "#FFD23F" },
  { key: "basket", label: "Basket", Icon: Basketball, color: "#FF7A1A" },
];

export default function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { setSport: setAppSport } = useSport();
  const [step, setStep] = useState(1);
  const [sport, setSport] = useState(null);
  const [team, setTeam] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);

  // Recherche d'équipe (debounce) parmi les équipes déjà en base.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const id = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => setResults(r.data?.teams || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const finish = async (withTeam) => {
    setLoading(true);
    try {
      const favorites = withTeam && team ? [{ kind: "team", ref_id: team.team_id }] : [];
      const { data } = await api.post("/onboarding", { favorites, favorite_sport: sport });
      if (!isAccount(data)) throw new Error("invalid account");
      setUser(data);
      if (sport) setAppSport(sport);
      toast.success(t("onboarding.personalized"));
      navigate("/");
    } catch { toast.error(t("onboarding.error")); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grain relative px-5 py-8">
      <div className="relative z-10 max-w-md mx-auto fade-up">
        <Logo size="h-14" />
        <div className="flex items-center gap-1.5 mt-6">
          <span className={`h-1.5 flex-1 rounded-full ${step >= 1 ? "bg-gold" : "bg-white/10"}`} />
          <span className={`h-1.5 flex-1 rounded-full ${step >= 2 ? "bg-gold" : "bg-white/10"}`} />
        </div>

        {step === 1 && (
          <div data-testid="onboarding-step-sport">
            <h1 className="font-display text-3xl font-bold mt-6 tracking-tight">{t("onboarding.sport_title")}</h1>
            <p className="text-sm text-white/50 mt-2">{t("onboarding.sport_subtitle")}</p>
            <div className="grid grid-cols-1 gap-3 mt-8">
              {SPORTS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`onboard-sport-${s.key}`}
                  onClick={() => { setSport(s.key); setStep(2); }}
                  className={`hm-card p-4 flex items-center gap-4 transition-all ${sport === s.key ? "hm-card-active" : ""}`}
                >
                  <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: `${s.color}1A`, border: `1px solid ${s.color}55` }}>
                    <s.Icon size={24} weight="fill" color={s.color} />
                  </span>
                  <span className="text-base font-bold flex-1 text-left">{s.label}</span>
                  {sport === s.key && <Check size={20} weight="bold" color="#D4AF37" />}
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Button variant="ghost" data-testid="onboard-skip" onClick={() => finish(false)} disabled={loading}
                className="text-white/50 hover:text-white hover:bg-white/5">
                {t("onboarding.skip")}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step-team">
            <h1 className="font-display text-3xl font-bold mt-6 tracking-tight">{t("onboarding.team_title")}</h1>
            <p className="text-sm text-white/50 mt-2">{t("onboarding.team_subtitle")}</p>

            <div className="relative mt-6">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="onboard-team-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("onboarding.search_placeholder")}
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-gold/50 transition-colors"
              />
            </div>

            {team && (
              <div className="mt-3 hm-card p-3 flex items-center gap-3 hm-card-active" data-testid="onboard-team-selected">
                <TeamBadge team={team} size={36} />
                <span className="text-sm font-semibold flex-1">{team.name}</span>
                <Check size={18} weight="bold" color="#D4AF37" />
              </div>
            )}

            <div className="mt-3 space-y-2">
              {searching && <p className="text-[12px] text-white/35 py-2 text-center">{t("onboarding.searching")}</p>}
              {!searching && results.map((tm) => (
                <button
                  key={tm.team_id}
                  data-testid={`onboard-team-result-${tm.team_id}`}
                  onClick={() => setTeam(tm)}
                  className={`w-full hm-card p-3 flex items-center gap-3 transition-all ${team?.team_id === tm.team_id ? "hm-card-active" : ""}`}
                >
                  <TeamBadge team={tm} size={32} />
                  <span className="text-sm font-semibold flex-1 text-left">{tm.name}</span>
                </button>
              ))}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p className="text-[12px] text-white/35 py-2 text-center" data-testid="onboard-team-empty">{t("onboarding.no_team")}</p>
              )}
            </div>

            <div className="mt-8 flex gap-3">
              <Button variant="ghost" data-testid="onboard-skip" onClick={() => finish(false)} disabled={loading}
                className="text-white/50 hover:text-white hover:bg-white/5">
                {t("onboarding.skip")}
              </Button>
              <Button data-testid="onboard-finish" onClick={() => finish(true)} disabled={loading}
                className="flex-1 h-12 bg-gold text-black hover:bg-gold-dark font-bold glow-gold">
                {t("onboarding.finish")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
